import { useCallback, useEffect, useMemo, useState } from "react";
import { api, fmtMoney, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
    ArrowUpDown, TrendingUp, TrendingDown, RefreshCcw, Bell, Trash2,
    Info, Globe, ArrowRight,
} from "lucide-react";
import { Link } from "react-router-dom";
import {
    LineChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid,
} from "recharts";

/**
 * Unified Live Forex dashboard.
 *
 * Composed of five slabs:
 *   1. Top ticker cards (default 8 currencies, live rates + Δ%).
 *   2. Currency converter (any-to-any including INR).
 *   3. Historical chart (7d / 30d / 90d / 6M / 1Y).
 *   4. Currency exposure summary (per-currency spend, wallet balances,
 *      top foreign merchants) — from /api/forex/exposure.
 *   5. Threshold alerts — per-user CRUD with live "triggered" flag.
 *
 * Everything is wrapped in a data-accuracy note. Live rates are indicative;
 * accounting always uses statement-posted values.
 */

const FLAG = {
    USD: "🇺🇸", EUR: "🇪🇺", GBP: "🇬🇧", AED: "🇦🇪", SGD: "🇸🇬",
    JPY: "🇯🇵", AUD: "🇦🇺", CAD: "🇨🇦", CHF: "🇨🇭", THB: "🇹🇭",
    MYR: "🇲🇾", HKD: "🇭🇰", NZD: "🇳🇿", ZAR: "🇿🇦", SAR: "🇸🇦",
    OMR: "🇴🇲", QAR: "🇶🇦", KWD: "🇰🇼", BHD: "🇧🇭", CNY: "🇨🇳",
    INR: "🇮🇳",
};

const ALL_SYMBOLS = [
    "USD", "EUR", "GBP", "AED", "SGD", "JPY", "AUD", "CAD",
    "CHF", "THB", "MYR", "HKD", "NZD", "ZAR", "SAR", "OMR",
    "QAR", "KWD", "BHD", "CNY",
];

const RANGE_OPTS = [
    { key: "7d", label: "7D" },
    { key: "30d", label: "30D" },
    { key: "90d", label: "90D" },
    { key: "6m", label: "6M" },
    { key: "1y", label: "1Y" },
];

// ─── Ticker card ─────────────────────────────────────────────────────
function CurrencyCard({ item, active, onClick }) {
    const dir = item.direction;
    const positive = dir === "up";
    return (
        <button
            type="button"
            onClick={onClick}
            className={`text-left bg-white border rounded-2xl p-4 hover:shadow-md transition-all ${active ? "border-stone-900 shadow-md" : "border-stone-200"}`}
            data-testid={`fx-card-${item.code}`}
        >
            <div className="flex items-center gap-2">
                <span className="text-2xl leading-none" aria-hidden>{FLAG[item.code] || "🌐"}</span>
                <div>
                    <div className="font-semibold text-stone-900 text-sm tracking-wide">{item.code}/INR</div>
                    <div className="text-[11px] text-stone-500 truncate max-w-[130px]">{item.name}</div>
                </div>
                {dir && (
                    <span
                        className={`ml-auto inline-flex items-center gap-0.5 text-[11px] font-medium px-1.5 py-0.5 rounded-full ${positive ? "text-emerald-700 bg-emerald-50" : "text-rose-700 bg-rose-50"}`}
                    >
                        {positive ? <TrendingUp size={11} /> : <TrendingDown size={11} />}
                        {item.change_pct > 0 ? "+" : ""}
                        {item.change_pct}%
                    </span>
                )}
            </div>
            <div className="mt-3 text-2xl font-bold tabular-nums text-stone-900">
                ₹{Number(item.inr_per_unit).toLocaleString("en-IN", { maximumFractionDigits: 4 })}
            </div>
            <div className="text-[11px] text-stone-500 mt-1">1 {item.code} = ₹{Number(item.inr_per_unit).toFixed(2)}</div>
        </button>
    );
}

// ─── Currency Converter ──────────────────────────────────────────────
function Converter() {
    const [from, setFrom] = useState("USD");
    const [to, setTo] = useState("INR");
    const [amount, setAmount] = useState("100");
    const [result, setResult] = useState(null);
    const [busy, setBusy] = useState(false);

    const run = useCallback(async () => {
        const amt = Number(amount);
        if (!amt || amt <= 0) return;
        setBusy(true);
        try {
            const { data } = await api.get("/forex/rates/convert", {
                params: { from, to, amount: amt },
            });
            setResult(data);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    }, [from, to, amount]);

    useEffect(() => { run(); }, [run]);

    const swap = () => {
        setFrom(to);
        setTo(from);
    };

    return (
        <div className="bg-white border border-stone-200 rounded-2xl p-5" data-testid="fx-converter">
            <div className="flex items-center justify-between mb-4">
                <div>
                    <div className="eyebrow">Currency converter</div>
                    <div className="text-lg font-semibold text-stone-900 mt-1">Convert any ↔ any</div>
                </div>
                <Globe size={16} className="text-stone-400" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-3 items-end">
                <div>
                    <Label className="eyebrow">From</Label>
                    <div className="flex gap-2 mt-1.5">
                        <Select value={from} onValueChange={setFrom}>
                            <SelectTrigger className="w-28" data-testid="fx-conv-from">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="max-h-64">
                                <SelectItem value="INR">🇮🇳 INR</SelectItem>
                                {ALL_SYMBOLS.map((s) => (
                                    <SelectItem key={s} value={s}>{FLAG[s]} {s}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Input
                            type="number"
                            value={amount}
                            onChange={(e) => setAmount(e.target.value)}
                            className="flex-1"
                            data-testid="fx-conv-amount"
                        />
                    </div>
                </div>
                <button
                    onClick={swap}
                    className="mb-1 w-10 h-10 rounded-full border border-stone-200 flex items-center justify-center hover:border-stone-900 transition-colors self-end"
                    aria-label="Swap"
                    data-testid="fx-conv-swap"
                >
                    <ArrowUpDown size={14} />
                </button>
                <div>
                    <Label className="eyebrow">To</Label>
                    <div className="flex gap-2 mt-1.5">
                        <Select value={to} onValueChange={setTo}>
                            <SelectTrigger className="w-28" data-testid="fx-conv-to">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="max-h-64">
                                <SelectItem value="INR">🇮🇳 INR</SelectItem>
                                {ALL_SYMBOLS.map((s) => (
                                    <SelectItem key={s} value={s}>{FLAG[s]} {s}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Input
                            readOnly
                            value={result?.converted != null ? Number(result.converted).toLocaleString("en-IN", { maximumFractionDigits: 4 }) : ""}
                            placeholder={busy ? "…" : ""}
                            className="flex-1 bg-stone-50 tabular-nums font-medium"
                            data-testid="fx-conv-result"
                        />
                    </div>
                </div>
            </div>

            <div className="mt-3 flex items-center justify-between text-[12px] text-stone-500 flex-wrap gap-2">
                <span>
                    {result?.rate != null && (
                        <>Rate: 1 {from} = <span className="tabular-nums font-medium text-stone-800">{Number(result.rate).toFixed(4)} {to === from ? "" : to}</span></>
                    )}
                </span>
                <span>
                    {result?.as_of && `Updated ${new Date(result.as_of).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}`}
                </span>
            </div>
        </div>
    );
}

// ─── Historical chart ────────────────────────────────────────────────
function HistoryChart({ symbol, setSymbol }) {
    const [range, setRange] = useState("30d");
    const [series, setSeries] = useState([]);
    const [unavailable, setUnavailable] = useState(false);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        api.get("/forex/rates/history", { params: { symbol, range } })
            .then(({ data }) => {
                if (cancelled) return;
                setSeries(data.series || []);
                setUnavailable(!!data.history_unavailable);
            })
            .catch(() => { if (!cancelled) { setUnavailable(true); setSeries([]); } })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [symbol, range]);

    const stats = useMemo(() => {
        if (!series.length) return null;
        const values = series.map((s) => s.value);
        const first = values[0];
        const last = values[values.length - 1];
        const min = Math.min(...values);
        const max = Math.max(...values);
        const delta = last - first;
        const pct = first ? (delta / first) * 100 : 0;
        return { first, last, min, max, delta, pct };
    }, [series]);

    return (
        <div className="bg-white border border-stone-200 rounded-2xl p-5" data-testid="fx-history">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <div>
                    <div className="eyebrow">Historical trend</div>
                    <div className="text-lg font-semibold text-stone-900 mt-1">
                        {symbol}/INR &middot; {RANGE_OPTS.find(r => r.key === range)?.label}
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <Select value={symbol} onValueChange={setSymbol}>
                        <SelectTrigger className="w-24" data-testid="fx-hist-symbol">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="max-h-64">
                            {ALL_SYMBOLS.map((s) => (
                                <SelectItem key={s} value={s}>{FLAG[s]} {s}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <div className="flex items-center border border-stone-200 rounded-full overflow-hidden">
                        {RANGE_OPTS.map((r) => (
                            <button
                                key={r.key}
                                onClick={() => setRange(r.key)}
                                className={`px-3 py-1 text-xs font-medium ${range === r.key ? "bg-stone-900 text-white" : "text-stone-600 hover:text-stone-900"}`}
                                data-testid={`fx-hist-range-${r.key}`}
                            >
                                {r.label}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {stats && (
                <div className="flex items-center gap-4 mb-3 text-[12px] flex-wrap">
                    <span className="text-stone-500">Now <span className="tabular-nums text-stone-900 font-semibold">₹{stats.last.toFixed(4)}</span></span>
                    <span className={`tabular-nums font-medium ${stats.pct >= 0 ? "text-emerald-700" : "text-rose-700"}`}>
                        {stats.pct >= 0 ? "▲" : "▼"} {Math.abs(stats.pct).toFixed(2)}%
                    </span>
                    <span className="text-stone-500">Low <span className="tabular-nums text-stone-900">₹{stats.min.toFixed(4)}</span></span>
                    <span className="text-stone-500">High <span className="tabular-nums text-stone-900">₹{stats.max.toFixed(4)}</span></span>
                </div>
            )}

            {loading ? (
                <div className="h-64 flex items-center justify-center text-sm text-stone-500">Loading chart…</div>
            ) : unavailable || !series.length ? (
                <div className="h-64 flex items-center justify-center flex-col gap-1 text-sm text-stone-500 bg-stone-50 rounded-lg">
                    <Info size={16} />
                    Historical data temporarily unavailable.
                    <span className="text-[11px]">Try again later — this depends on our upstream data feed.</span>
                </div>
            ) : (
                <div className="h-64" data-testid="fx-hist-chart">
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={series} margin={{ top: 5, right: 8, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f5f5f4" />
                            <XAxis dataKey="date" tick={{ fontSize: 10 }} minTickGap={30} />
                            <YAxis tick={{ fontSize: 10 }} domain={["dataMin - 0.5", "dataMax + 0.5"]} />
                            <Tooltip formatter={(v) => `₹${Number(v).toFixed(4)}`} />
                            <Line type="monotone" dataKey="value" stroke="#0f172a" strokeWidth={2} dot={false} />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            )}
        </div>
    );
}

// ─── Exposure ────────────────────────────────────────────────────────
function Exposure() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        api.get("/forex/exposure")
            .then((r) => setData(r.data))
            .catch(() => setData({ per_currency: [], top_merchants: [], total_international_spend_inr: 0 }))
            .finally(() => setLoading(false));
    }, []);

    if (loading) return null;
    const rows = data?.per_currency || [];
    const merchants = data?.top_merchants || [];

    return (
        <div className="bg-white border border-stone-200 rounded-2xl p-5" data-testid="fx-exposure">
            <div className="flex items-start justify-between mb-4 flex-wrap gap-2">
                <div>
                    <div className="eyebrow">Currency exposure &middot; {data?.month}</div>
                    <div className="text-lg font-semibold text-stone-900 mt-1">
                        {fmtMoney(data?.total_international_spend_inr || 0)} international spend
                    </div>
                </div>
                <div className="text-[12px] text-stone-500">
                    Fees paid: <span className="tabular-nums font-medium text-stone-800">{fmtMoney(data?.total_fees_inr || 0)}</span>
                </div>
            </div>

            {rows.length === 0 ? (
                <div className="text-sm text-stone-500 bg-stone-50 rounded-lg p-6 text-center">
                    No international spend or forex wallets to report yet. Load a forex card or record a foreign-currency
                    expense to see exposure here.
                </div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="dense-table w-full">
                        <thead>
                            <tr>
                                <th>Currency</th>
                                <th className="text-right">Spend (native)</th>
                                <th className="text-right">Spend (₹)</th>
                                <th className="text-right">Fees (₹)</th>
                                <th className="text-right">Wallet balance</th>
                                <th className="text-right">Txns</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => (
                                <tr key={r.currency} data-testid={`fx-exp-${r.currency}`}>
                                    <td>
                                        <span className="text-lg mr-1">{FLAG[r.currency] || "🌐"}</span>
                                        <span className="font-medium">{r.currency}</span>
                                        <span className="text-[11px] text-stone-500 ml-1">· {r.name}</span>
                                    </td>
                                    <td className="text-right tabular-nums">
                                        {r.spend_native ? `${r.spend_native.toLocaleString()} ${r.currency}` : "—"}
                                    </td>
                                    <td className="text-right tabular-nums">{r.spend_inr ? fmtMoney(r.spend_inr) : "—"}</td>
                                    <td className="text-right tabular-nums text-stone-600">{r.fees ? fmtMoney(r.fees) : "—"}</td>
                                    <td className="text-right tabular-nums font-medium">
                                        {r.wallet_balance ? `${r.wallet_balance.toLocaleString()} ${r.currency}` : "—"}
                                    </td>
                                    <td className="text-right tabular-nums">{r.txn_count}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {merchants.length > 0 && (
                <>
                    <div className="eyebrow mt-6 mb-2">Top foreign merchants this month</div>
                    <ul className="space-y-1.5">
                        {merchants.map((m, i) => (
                            <li key={`${m.merchant}-${i}`} className="flex items-center justify-between text-sm">
                                <span className="truncate">
                                    <span className="mr-2 text-stone-500 tabular-nums">{i + 1}.</span>
                                    {m.merchant}
                                    <span className="text-[11px] text-stone-500 ml-2">{FLAG[m.currency]} {m.currency} · {m.count} txn</span>
                                </span>
                                <span className="tabular-nums font-medium">{fmtMoney(m.spend_inr)}</span>
                            </li>
                        ))}
                    </ul>
                </>
            )}
        </div>
    );
}

// ─── Alerts ──────────────────────────────────────────────────────────
function Alerts() {
    const [alerts, setAlerts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState({ symbol: "USD", direction: "above", threshold: "", label: "" });

    const load = useCallback(() => {
        setLoading(true);
        api.get("/forex/alerts")
            .then((r) => setAlerts(r.data?.alerts || []))
            .catch(() => setAlerts([]))
            .finally(() => setLoading(false));
    }, []);
    useEffect(() => { load(); }, [load]);

    const create = async () => {
        const t = Number(form.threshold);
        if (!t || t <= 0) {
            toast.error("Enter a positive threshold rate.");
            return;
        }
        try {
            await api.post("/forex/alerts", { ...form, threshold: t });
            toast.success(`Alert set: ${form.symbol} ${form.direction} ₹${t}`);
            setForm({ symbol: "USD", direction: "above", threshold: "", label: "" });
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const del = async (id) => {
        try {
            await api.delete(`/forex/alerts/${id}`);
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    return (
        <div className="bg-white border border-stone-200 rounded-2xl p-5" data-testid="fx-alerts">
            <div className="flex items-center gap-2 mb-4">
                <Bell size={16} className="text-stone-500" />
                <div className="eyebrow">Rate alerts</div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 items-end">
                <div>
                    <Label className="eyebrow">Currency</Label>
                    <Select value={form.symbol} onValueChange={(v) => setForm((s) => ({ ...s, symbol: v }))}>
                        <SelectTrigger data-testid="fx-alert-symbol"><SelectValue /></SelectTrigger>
                        <SelectContent className="max-h-64">
                            {ALL_SYMBOLS.map((s) => (
                                <SelectItem key={s} value={s}>{FLAG[s]} {s}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <div>
                    <Label className="eyebrow">When</Label>
                    <Select value={form.direction} onValueChange={(v) => setForm((s) => ({ ...s, direction: v }))}>
                        <SelectTrigger data-testid="fx-alert-direction"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="above">Crosses above</SelectItem>
                            <SelectItem value="below">Falls below</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <div>
                    <Label className="eyebrow">Rate (₹)</Label>
                    <Input
                        type="number"
                        value={form.threshold}
                        onChange={(e) => setForm((s) => ({ ...s, threshold: e.target.value }))}
                        placeholder="e.g. 85"
                        data-testid="fx-alert-threshold"
                    />
                </div>
                <div>
                    <Label className="eyebrow">Label (optional)</Label>
                    <Input
                        value={form.label}
                        onChange={(e) => setForm((s) => ({ ...s, label: e.target.value }))}
                        placeholder="e.g. Buy USD for trip"
                        data-testid="fx-alert-label"
                    />
                </div>
                <Button onClick={create} className="bg-stone-900 hover:bg-stone-800 text-white" data-testid="fx-alert-create">
                    Add alert
                </Button>
            </div>

            {loading ? (
                <div className="text-sm text-stone-500 mt-4">Loading…</div>
            ) : alerts.length === 0 ? (
                <div className="mt-4 text-sm text-stone-500 bg-stone-50 rounded-lg p-4 text-center">
                    No alerts yet. Set thresholds above to get notified when a rate crosses.
                </div>
            ) : (
                <ul className="mt-4 divide-y divide-stone-100">
                    {alerts.map((a) => (
                        <li key={a.id} className="flex items-center gap-3 py-2.5 text-sm" data-testid={`fx-alert-row-${a.id}`}>
                            <span className="text-lg">{FLAG[a.symbol]}</span>
                            <div className="flex-1 min-w-0">
                                <div className="truncate">
                                    <span className="font-medium">{a.symbol}/INR</span>{" "}
                                    <span className="text-stone-500">{a.direction}</span>{" "}
                                    <span className="tabular-nums font-medium">₹{a.threshold}</span>
                                    {a.label && <span className="text-stone-500"> · {a.label}</span>}
                                </div>
                                <div className="text-[11px] text-stone-500 tabular-nums">
                                    Current: ₹{a.current_rate ? Number(a.current_rate).toFixed(4) : "—"}
                                </div>
                            </div>
                            {a.triggered && (
                                <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
                                    Triggered
                                </span>
                            )}
                            <button
                                onClick={() => del(a.id)}
                                className="text-stone-400 hover:text-rose-600"
                                aria-label="Delete"
                                data-testid={`fx-alert-del-${a.id}`}
                            >
                                <Trash2 size={14} />
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

// ─── Main Forex Dashboard ────────────────────────────────────────────
export default function ForexDashboard() {
    const [rates, setRates] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedSymbol, setSelectedSymbol] = useState("USD");
    const [refreshing, setRefreshing] = useState(false);

    const load = useCallback(async (force = false) => {
        if (force) setRefreshing(true);
        try {
            const { data } = await api.get("/forex/rates", { params: { refresh: force ? true : undefined } });
            setRates(data);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);
    // Auto-refresh every 5 minutes
    useEffect(() => {
        const t = setInterval(() => load(), 5 * 60 * 1000);
        return () => clearInterval(t);
    }, [load]);

    return (
        <div className="max-w-[1400px] mx-auto" data-testid="forex-dashboard">
            <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
                <div>
                    <div className="eyebrow">Command Centre</div>
                    <h1 className="font-display text-4xl sm:text-5xl text-stone-900 leading-none mt-2">
                        Live forex.
                    </h1>
                    <p className="text-sm text-stone-500 mt-3 max-w-xl">
                        Real-time exchange rates against INR, historical trends, currency exposure across your
                        forex cards, and rate alerts — all in one place.
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    {rates && (
                        <div className="text-[11px] text-stone-500 text-right">
                            {rates.cached ? "cached" : "live"} · {rates.provider}
                            <div className="tabular-nums">
                                {rates.as_of && new Date(rates.as_of).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                            </div>
                        </div>
                    )}
                    <Button
                        onClick={() => load(true)}
                        variant="outline"
                        size="sm"
                        disabled={refreshing}
                        data-testid="fx-refresh-btn"
                    >
                        <RefreshCcw size={13} className={`mr-1.5 ${refreshing ? "animate-spin" : ""}`} />
                        Refresh
                    </Button>
                </div>
            </div>

            {/* Data accuracy note */}
            <div className="mb-6 rounded-xl bg-amber-50 border border-amber-200 p-3 flex items-start gap-2" data-testid="fx-note">
                <Info size={14} className="text-amber-700 mt-0.5 shrink-0" />
                <div className="text-[12px] text-amber-900">
                    Live forex rates are indicative. Final card billing may vary based on bank exchange rate, network
                    rate, forex markup, GST, and posting date. For accounting, final statement amount should be
                    treated as the source of truth.
                </div>
            </div>

            {/* Ticker grid */}
            {loading ? (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
                    {[...Array(8)].map((_, i) => (
                        <div key={i} className="h-28 bg-stone-100 rounded-2xl animate-pulse" />
                    ))}
                </div>
            ) : (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8" data-testid="fx-cards-grid">
                    {(rates?.items || []).map((it) => (
                        <CurrencyCard
                            key={it.code}
                            item={it}
                            active={selectedSymbol === it.code}
                            onClick={() => setSelectedSymbol(it.code)}
                        />
                    ))}
                </div>
            )}

            {/* Converter + history side-by-side */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
                <Converter />
                <HistoryChart symbol={selectedSymbol} setSymbol={setSelectedSymbol} />
            </div>

            {/* Exposure */}
            <div className="mb-6">
                <Exposure />
            </div>

            {/* Alerts */}
            <div className="mb-6">
                <Alerts />
            </div>

            {/* Deep links */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {[
                    { to: "/forex-cards", label: "Forex Cards", desc: "Multi-currency wallets, expiry & limits" },
                    { to: "/forex-expenses", label: "Forex Expenses", desc: "International spend + trips" },
                    { to: "/forex-reloads", label: "Forex Reloads", desc: "Top-ups, GST & bank charges" },
                ].map((c) => (
                    <Link
                        key={c.to}
                        to={c.to}
                        className="bg-white border border-stone-200 rounded-2xl p-4 hover:border-stone-900 transition-colors group"
                        data-testid={`fx-deeplink-${c.to.slice(1)}`}
                    >
                        <div className="flex items-center justify-between">
                            <div className="font-semibold text-stone-900">{c.label}</div>
                            <ArrowRight size={14} className="text-stone-400 group-hover:text-stone-900 group-hover:translate-x-0.5 transition-all" />
                        </div>
                        <div className="text-[12px] text-stone-500 mt-1">{c.desc}</div>
                    </Link>
                ))}
            </div>
        </div>
    );
}
